import { Card, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import QuestionnaireType, { QuestionnaireQuestion } from "../../../../interfaces/feedback/QuestionnaireType";
import FeedbackStatisticsType, { RoleFeedbackStatistics } from "../../../../interfaces/statistics/FeedbackStatisticsType";
import { STUDENT_QUESTIONNAIRE, TEACHER_QUESTIONNAIRE } from "../../../shared/feedback-page/questionnaire";

interface QuestionnaireStatisticsProps {
  statistics: FeedbackStatisticsType;
}

const baseClass = "statistics-admin";

const roles: Array<{ label: string; questionnaire: QuestionnaireType; key: keyof FeedbackStatisticsType }> = [
  { label: "Student", questionnaire: STUDENT_QUESTIONNAIRE, key: "student" },
  { label: "Teacher", questionnaire: TEACHER_QUESTIONNAIRE, key: "teacher" },
];

// Answers in questionnaire order: how many users picked each answer and the percentage
// of all platform participants of that role.
function AnswerCell({ question, stats }: { question: QuestionnaireQuestion; stats: RoleFeedbackStatistics }) {
  const counts = stats.answers[question.id] ?? {};
  const participants = stats.participants;

  return (
    <TableCell className={`${baseClass}__questionnaire-cell`}>
      {question.answers.map((answer) => {
        const count = counts[answer.id] ?? 0;
        const percent = participants ? Math.round((count / participants) * 100) : 0;
        return (
          <div key={answer.id} className={`${baseClass}__questionnaire-answer`}>
            <Typography fontSize={12}>{answer.text}</Typography>
            <Typography fontSize={12} fontWeight={500}>{`${count} (${percent}%)`}</Typography>
          </div>
        );
      })}
    </TableCell>
  );
}

export default function QuestionnaireStatistics({ statistics }: QuestionnaireStatisticsProps) {
  return (
    <Card className={`${baseClass}__questionnaire`}>
      <div className={`${baseClass}__questionnaire-header`}>
        <Typography fontSize={14} fontWeight={500}>Questionnaire</Typography>
        <div>
          <Typography fontSize={12}>{`Platform participants: students ${statistics.student.participants}, teachers ${statistics.teacher.participants}`}</Typography>
          <Typography fontSize={12}>{`Questionnaire responses: students ${statistics.student.responses}, teachers ${statistics.teacher.responses}`}</Typography>
        </div>
      </div>
      <div className={`${baseClass}__questionnaire-scroll`}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {roles.map(({ label, questionnaire }) => (
                <TableCell key={label} align="center" colSpan={questionnaire.questions.length} className={`${baseClass}__questionnaire-cell`}>
                  <Typography fontSize={13} fontWeight={500}>{label}</Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {roles.map(({ label, questionnaire }) =>
                questionnaire.questions.map((question, index) => (
                  <TableCell key={`${label}-${question.id}`} align="center" className={`${baseClass}__questionnaire-cell`}>
                    <Tooltip title={question.text} arrow>
                      <Typography fontSize={12} fontWeight={500}>{`Q${index + 1}`}</Typography>
                    </Tooltip>
                  </TableCell>
                ))
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {roles.map(({ label, questionnaire, key }) =>
                questionnaire.questions.map((question) => <AnswerCell key={`${label}-${question.id}`} question={question} stats={statistics[key]} />)
              )}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
